import React, { useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { Search, Printer, CheckSquare, Slash, RotateCcw, Grid } from "react-feather";
import SectionCard from "../../../../components/common/SectionCard";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { useCanEditCustomers } from "../useCustomerPermissions";
import {
  useGetCustomerQrLabelsQuery,
  useLazyGetCustomerLocationQrCodeQuery,
  useMarkQrLabelsPrintedMutation,
  useSetQrLabelsNotRequiredMutation,
} from "../customersApi";
import { blobToDataUrl, openStickerWindow, writeStickers } from "../qrStickerPrint";

// One-time job (2026-09-17): print a QR sticker for every existing customer
// site, and know which ones are done. The QR token itself proves nothing (it
// is created the first time anyone opens a customer), so printing is recorded
// here; a site already scanned in the field also counts as printed. Suppliers
// and similar records that never get a sticker are marked "QR not needed".

const STATUSES = [
  { key: "PENDING", label: "Not printed" },
  { key: "PRINTED", label: "Printed" },
  { key: "NOT_REQUIRED", label: "QR not needed" },
  { key: "ALL", label: "All" },
];
const STATUS_BADGE = {
  PENDING: { cls: "badge-linewarning", text: "Not printed" },
  PRINTED: { cls: "badge-linesuccess", text: "Printed" },
  NOT_REQUIRED: { cls: "badge-linesecondary", text: "QR not needed" },
};
const REASONS = ["Supplier", "Not a service customer", "Duplicate record", "Customer closed"];
const PAGE_SIZE = 100;
// One print job at a time stays small enough to reprint if the roll jams.
const MAX_PRINT = 100;
const FETCH_PARALLEL = 4;

const fmtDate = (v) => (v ? String(v).substring(0, 10) : "");

const NotNeededModal = ({ show, count, onClose, onSave, saving }) => {
  const [reason, setReason] = useState("Supplier");
  return (
    <Modal show={show} onHide={saving ? undefined : onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>QR not needed</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">
          {count} selected customer site{count === 1 ? "" : "s"} will leave the "Not printed" list. You can move them back
          later from "QR not needed".
        </p>
        <label className="form-label">Reason <span className="text-danger">*</span></label>
        <div className="d-flex flex-wrap gap-1 mb-2">
          {REASONS.map((r) => (
            <button key={r} type="button"
              className={`btn btn-sm ${reason === r ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => setReason(r)}>
              {r}
            </button>
          ))}
        </div>
        <input className="form-control" maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" disabled={saving || !reason.trim()} onClick={() => onSave(reason.trim())}>
          {saving ? "Saving..." : "Mark QR not needed"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const CustomerQrStickersPage = () => {
  const { selectedBranchId } = useBranchFilter();
  const canEdit = useCanEditCustomers();
  const [status, setStatus] = useState("PENDING");
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [shown, setShown] = useState(PAGE_SIZE);
  const [picked, setPicked] = useState(() => new Set());
  const [printing, setPrinting] = useState(false);
  const [confirmIds, setConfirmIds] = useState(null); // ids just sent to the printer
  const [alreadyIds, setAlreadyIds] = useState(null); // ids to mark as printed earlier
  const [notNeededOpen, setNotNeededOpen] = useState(false);

  const { data: rows = [], isFetching, isLoading } = useGetCustomerQrLabelsQuery({
    ...(selectedBranchId != null ? { branchId: selectedBranchId } : {}),
    includeInactive,
  });
  const [triggerQr] = useLazyGetCustomerLocationQrCodeQuery();
  const [markPrinted, markState] = useMarkQrLabelsPrintedMutation();
  const [setNotRequired, notRequiredState] = useSetQrLabelsNotRequiredMutation();

  const searched = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (r) =>
        r.custName?.toLowerCase().includes(term) ||
        r.siteLabel?.toLowerCase().includes(term) ||
        r.siteText?.toLowerCase().includes(term) ||
        String(r.custCode ?? "").includes(term)
    );
  }, [rows, search]);

  const counts = useMemo(() => {
    const c = { ALL: searched.length, PENDING: 0, PRINTED: 0, NOT_REQUIRED: 0 };
    searched.forEach((r) => { c[r.qrStatus] = (c[r.qrStatus] || 0) + 1; });
    return c;
  }, [searched]);

  const filtered = useMemo(
    () => (status === "ALL" ? searched : searched.filter((r) => r.qrStatus === status)),
    [searched, status]
  );
  const visible = filtered.slice(0, shown);

  // Ticks only count while the row is still in the filtered list.
  const pickedRows = useMemo(() => filtered.filter((r) => picked.has(r.locationId)), [filtered, picked]);
  const allVisiblePicked = visible.length > 0 && visible.every((r) => picked.has(r.locationId));
  const togglePick = (id) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAllVisible = () =>
    setPicked((prev) => {
      const next = new Set(prev);
      visible.forEach((r) => (allVisiblePicked ? next.delete(r.locationId) : next.add(r.locationId)));
      return next;
    });
  const clearPicked = () => setPicked(new Set());
  const pickFirst = (n) => setPicked(new Set(filtered.slice(0, n).map((r) => r.locationId)));

  const changeStatus = (key) => {
    setStatus(key);
    setShown(PAGE_SIZE);
    clearPicked();
  };

  const handlePrint = async () => {
    const list = pickedRows;
    if (list.length === 0) return;
    if (list.length > MAX_PRINT) {
      toast.error(`Print at most ${MAX_PRINT} stickers at a time.`);
      return;
    }
    const win = openStickerWindow(list.length);
    if (!win) {
      toast.error("The browser blocked the print window - allow pop-ups for this site.");
      return;
    }
    setPrinting(true);
    try {
      const stickers = new Array(list.length);
      let next = 0;
      const worker = async () => {
        while (next < list.length) {
          const i = next++;
          const r = list[i];
          const blob = await triggerQr({ branchId: r.branchId, custId: r.custId, locationId: r.locationId }, true).unwrap();
          stickers[i] = { ...r, dataUrl: await blobToDataUrl(blob) };
        }
      };
      await Promise.all(Array.from({ length: Math.min(FETCH_PARALLEL, list.length) }, worker));
      writeStickers(win, stickers);
      setConfirmIds(list.map((r) => r.locationId));
    } catch (e) {
      win.close();
      toast.error(e?.message || "Could not prepare the QR stickers");
    } finally {
      setPrinting(false);
    }
  };

  const recordPrinted = async (ids) => {
    try {
      const res = await markPrinted(ids).unwrap();
      toast.success(`${res?.count ?? ids.length} marked as printed`);
      setConfirmIds(null);
      setAlreadyIds(null);
      clearPicked();
    } catch (e) {
      toast.error(e?.message || "Could not record the print");
    }
  };

  const saveNotNeeded = async (reason) => {
    const ids = pickedRows.filter((r) => r.qrStatus !== "NOT_REQUIRED").map((r) => r.locationId);
    try {
      const res = await setNotRequired({ locationIds: ids, notRequired: true, reason }).unwrap();
      toast.success(`${res?.count ?? ids.length} marked as QR not needed`);
      setNotNeededOpen(false);
      clearPicked();
    } catch (e) {
      toast.error(e?.message || "Could not save");
    }
  };

  const moveBack = async () => {
    const ids = pickedRows.filter((r) => r.qrStatus === "NOT_REQUIRED").map((r) => r.locationId);
    try {
      const res = await setNotRequired({ locationIds: ids, notRequired: false }).unwrap();
      toast.success(`${res?.count ?? ids.length} moved back`);
      clearPicked();
    } catch (e) {
      toast.error(e?.message || "Could not save");
    }
  };

  const n = pickedRows.length;
  const pickedNotNeeded = pickedRows.filter((r) => r.qrStatus === "NOT_REQUIRED").length;
  const pickedPending = pickedRows.filter((r) => r.qrStatus === "PENDING").length;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>QR Stickers</h4>
              <h6>Print customer QR stickers (70 x 40 mm roll) and keep track of which customers are done</h6>
            </div>
          </div>
        </div>

        <SectionCard
          icon={Grid}
          title={isLoading ? "Loading..." : `${filtered.length} site${filtered.length === 1 ? "" : "s"}`}
          right={
            <div className="form-check form-switch mb-0">
              <input className="form-check-input" type="checkbox" id="qr-inactive" checked={includeInactive}
                onChange={(e) => { setIncludeInactive(e.target.checked); clearPicked(); }} />
              <label className="form-check-label small" htmlFor="qr-inactive">Include inactive customers</label>
            </div>
          }
        >
          <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
            <div className="btn-group btn-group-sm" role="group">
              {STATUSES.map((s) => (
                <button key={s.key} type="button"
                  className={`btn ${status === s.key ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => changeStatus(s.key)}>
                  {s.label} <span className="ms-1">({counts[s.key] || 0})</span>
                </button>
              ))}
            </div>
            <div className="input-group input-group-sm" style={{ width: 260 }}>
              <span className="input-group-text"><Search size={14} /></span>
              <input type="text" className="form-control" placeholder="Customer, code, site, address"
                value={search} onChange={(e) => { setSearch(e.target.value); setShown(PAGE_SIZE); }} />
            </div>
            {isFetching && !isLoading && <small className="text-muted">Refreshing…</small>}
          </div>

          {/* Actions for the ticked rows */}
          <div className="d-flex align-items-center flex-wrap gap-2 mb-3 p-2 rounded" style={{ background: "#f7f3f3" }}>
            <strong className="small me-1">{n ? `${n} selected` : "Tick customers to print"}</strong>
            {filtered.length > 0 && n === 0 && (
              <button type="button" className="btn btn-sm btn-link px-1"
                onClick={() => pickFirst(Math.min(MAX_PRINT, filtered.length))}>
                Select first {Math.min(MAX_PRINT, filtered.length)}
              </button>
            )}
            <button type="button" className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1"
              disabled={n === 0 || n > MAX_PRINT || printing} onClick={handlePrint}
              title={n > MAX_PRINT ? `At most ${MAX_PRINT} per print` : "Print one sticker per selected site"}>
              <Printer size={14} /> {printing ? "Preparing…" : `Print stickers${n ? ` (${n})` : ""}`}
            </button>
            {pickedPending > 0 && (
              <button type="button" className="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1"
                onClick={() => setAlreadyIds(pickedRows.filter((r) => r.qrStatus === "PENDING").map((r) => r.locationId))}
                title="These already have a sticker from before - take them off the list without printing">
                <CheckSquare size={14} /> Already has sticker ({pickedPending})
              </button>
            )}
            {canEdit && n > 0 && pickedNotNeeded < n && (
              <button type="button" className="btn btn-sm btn-outline-dark d-inline-flex align-items-center gap-1"
                onClick={() => setNotNeededOpen(true)}>
                <Slash size={14} /> QR not needed
              </button>
            )}
            {canEdit && pickedNotNeeded > 0 && (
              <button type="button" className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                disabled={notRequiredState.isLoading} onClick={moveBack}>
                <RotateCcw size={14} /> Needs QR after all ({pickedNotNeeded})
              </button>
            )}
            {n > 0 && (
              <button type="button" className="btn btn-sm btn-link px-1" onClick={clearPicked}>Clear selection</button>
            )}
            {n > MAX_PRINT && <small className="text-danger">Print at most {MAX_PRINT} at a time.</small>}
          </div>

          {!isLoading && filtered.length === 0 && (
            <p className="text-muted mb-0">
              {status === "PENDING" && !search ? "Every customer here has a sticker or is marked QR not needed." : "Nothing matches."}
            </p>
          )}

          {visible.length > 0 && (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 28 }}>
                      <input type="checkbox" className="form-check-input" checked={allVisiblePicked}
                        onChange={toggleAllVisible} title="Select all shown" aria-label="Select all shown" />
                    </th>
                    <th>Customer</th>
                    <th>Site / address</th>
                    <th>Status</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => {
                    const badge = STATUS_BADGE[r.qrStatus];
                    return (
                      <tr key={r.locationId} className={r.isActive ? "" : "text-muted"}>
                        <td>
                          <input type="checkbox" className="form-check-input" checked={picked.has(r.locationId)}
                            onChange={() => togglePick(r.locationId)} aria-label={`Select ${r.custName}`} />
                        </td>
                        <td>
                          <span className="fw-bold">{r.custName || `Customer ${r.custId}`}</span>
                          <small className="d-block text-muted">
                            #{r.custCode} · {r.branchCode}
                            {!r.isActive && <span className="badge badge-linesecondary ms-1">Inactive</span>}
                          </small>
                        </td>
                        <td>
                          {r.siteLabel && r.siteLabel !== "Main" && <div className="fw-semibold small">{r.siteLabel}</div>}
                          <small className="text-muted">{r.siteText || "—"}</small>
                        </td>
                        <td>
                          <span className={`badge ${badge?.cls || "badge-linesecondary"}`}>{badge?.text || r.qrStatus}</span>
                          {r.lastScannedAt && (
                            <small className="d-block text-success">Scanned on site {fmtDate(r.lastScannedAt)}</small>
                          )}
                        </td>
                        <td className="small text-muted">
                          {r.printCount > 0 && (
                            <div>
                              Printed {fmtDate(r.lastPrintedAt)}{r.lastPrintedByName ? ` by ${r.lastPrintedByName}` : ""}
                              {r.printCount > 1 ? ` (${r.printCount} times)` : ""}
                            </div>
                          )}
                          {r.qrStatus === "NOT_REQUIRED" && (
                            <div>
                              {r.notRequiredReason}
                              {r.notRequiredByName ? ` · ${r.notRequiredByName}` : ""} {fmtDate(r.notRequiredAt)}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {filtered.length > visible.length && (
            <div className="text-center mt-3">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShown((s) => s + PAGE_SIZE)}>
                Show more ({filtered.length - visible.length} left)
              </button>
            </div>
          )}
        </SectionCard>

        {/* Asked after printing, so a jammed roll or a cancelled print dialog
            does not take customers off the list. */}
        <Modal show={!!confirmIds} onHide={() => setConfirmIds(null)} centered backdrop="static">
          <Modal.Header>
            <Modal.Title>Did the stickers print OK?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Answer Yes once all {confirmIds?.length} sticker{confirmIds?.length === 1 ? "" : "s"} came out correctly - they
            will be marked as printed. Answer No to leave them on the list to print again.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setConfirmIds(null)} disabled={markState.isLoading}>No, not printed</Button>
            <Button variant="success" onClick={() => recordPrinted(confirmIds)} disabled={markState.isLoading}>
              {markState.isLoading ? "Saving..." : "Yes, mark as printed"}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={!!alreadyIds} onHide={() => setAlreadyIds(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Already has a sticker</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Mark {alreadyIds?.length} customer site{alreadyIds?.length === 1 ? "" : "s"} as printed without printing? Use this
            only for customers whose sticker was printed and put up before this screen existed.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setAlreadyIds(null)} disabled={markState.isLoading}>Cancel</Button>
            <Button variant="success" onClick={() => recordPrinted(alreadyIds)} disabled={markState.isLoading}>
              {markState.isLoading ? "Saving..." : "Mark as printed"}
            </Button>
          </Modal.Footer>
        </Modal>

        {notNeededOpen && (
          <NotNeededModal show count={n - pickedNotNeeded} saving={notRequiredState.isLoading}
            onClose={() => setNotNeededOpen(false)} onSave={saveNotNeeded} />
        )}
      </div>
    </div>
  );
};

export default CustomerQrStickersPage;
